// Admin AI Credit Queue (Sprint 2 / 7B).
// Shows all client credit-profile analyses; click into one to drill down into
// the dispute candidates and trigger Sprint 3 letter generation.
import { useCallback, useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { apiCall } from "../auth/AuthContext";
import { ChevronRight, FileText, Loader2, Sparkles, Mail, X, CheckSquare, Square } from "lucide-react";

const ROUNDS = [
  { value: 1, label: "Round 1 — Bureaus (FCRA §611)" },
  { value: 2, label: "Round 2 — Furnishers (FCRA §623)" },
  { value: 3, label: "Round 3 — CFPB Escalation" },
];
const BUREAUS = ["Experian", "Equifax", "TransUnion"];

export default function AdminCreditQueue() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);    // selected profile
  const [drafting, setDrafting] = useState(false);
  const [draftMsg, setDraftMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await apiCall("/api/admin/credit/queue");
      return d.records || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const rows = await load();
      if (cancelled) return;
      setRecords(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load]);

  return (
    <AdminLayout title="AI CREDIT ANALYZER" kicker="Sprint 2 · 7B">
      {loading ? (
        <div className="py-20 flex items-center justify-center text-jc-muted gap-3"><Loader2 className="animate-spin" size={18}/> Loading…</div>
      ) : records.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-jc-gold/20 rounded-xl">
          <FileText size={36} className="mx-auto text-jc-gold/50 mb-3" />
          <p className="text-jc-muted">No credit profile uploads yet. Clients upload via their dashboard.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5 space-y-2" data-testid="admin-credit-list">
            {records.map((r) => (
              <button
                key={r.id}
                onClick={() => { setActive(r); setDraftMsg(""); }}
                data-testid={`credit-row-${r.id}`}
                className={`w-full text-left border ${active?.id === r.id ? "border-jc-gold bg-jc-gold/5" : "border-jc-gold/15 hover:border-jc-gold/40"} px-5 py-4 transition-colors`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-jc-white">{r.client_first_name || r.client_email}</p>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mt-0.5">{r.client_email}</p>
                  </div>
                  <ChevronRight size={16} className="text-jc-gold/60"/>
                </div>
                <div className="mt-3 flex items-center gap-4 text-[11px] font-mono uppercase tracking-[0.18em]">
                  <span className={`${r.status === "analyzed" ? "text-emerald-300" : r.status === "error" ? "text-red-300" : "text-jc-muted"}`}>{r.status}</span>
                  <span className="text-jc-muted">FICO {r.summary?.headline_score ?? "—"}</span>
                  <span className="text-jc-gold">{(r.profile?.dispute_candidates || []).length} candidates</span>
                </div>
              </button>
            ))}
          </div>

          <div className="md:col-span-7">
            {active ? (
              <ProfileDetail
                key={active.id}
                profile={active}
                drafting={drafting}
                draftMsg={draftMsg}
                onDraft={async ({ indices, round, bureaus }) => {
                  setDrafting(true);
                  setDraftMsg("");
                  try {
                    const body = { item_indices: indices, round_number: round };
                    if (round === 1 || round === 3) body.bureaus = bureaus;
                    const d = await apiCall(`/api/admin/disputes/draft/${active.id}`, {
                      method: "POST", body: JSON.stringify(body),
                    });
                    setDraftMsg(`✔ ${d.created} letter(s) drafted. Review in Dispute Letters.`);
                  } catch (e) {
                    setDraftMsg(`× ${e.message || "Draft failed"}`);
                  } finally { setDrafting(false); }
                }}
              />
            ) : (
              <div className="py-20 text-center border border-dashed border-jc-gold/15 rounded-xl text-jc-muted">
                Select a client to view dispute candidates.
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function ProfileDetail({ profile, drafting, draftMsg, onDraft }) {
  const candidates = profile?.profile?.dispute_candidates || [];
  const [selected, setSelected] = useState(() => new Set(candidates.map((_, i) => i)));
  const [round, setRound] = useState(1);
  const [bureaus, setBureaus] = useState(new Set(BUREAUS));

  const toggle = (i) => {
    const next = new Set(selected);
    next.has(i) ? next.delete(i) : next.add(i);
    setSelected(next);
  };
  const toggleBureau = (b) => {
    const next = new Set(bureaus);
    next.has(b) ? next.delete(b) : next.add(b);
    setBureaus(next);
  };

  return (
    <div className="border border-jc-gold/20 bg-jc-navy/40 p-6 rounded-xl" data-testid="admin-credit-detail">
      <h3 className="font-display text-2xl text-jc-white">{profile.client_first_name || profile.client_email}</h3>
      <p className="text-xs text-jc-muted mt-1 font-mono uppercase tracking-[0.18em]">{profile.client_email}</p>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Mini label="FICO" value={profile.summary?.headline_score ?? "—"} />
        <Mini label="Tradelines" value={profile.summary?.tradeline_count ?? "—"} />
        <Mini label="Negatives" value={profile.summary?.negative_items_count ?? 0} />
        <Mini label="Candidates" value={candidates.length} />
      </div>

      <div className="mt-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-2">◆ Dispute candidates</p>
        {candidates.length === 0 ? (
          <p className="text-sm text-jc-muted">No FCRA dispute candidates flagged for this profile.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {candidates.map((c, i) => (
              <label key={`${i}-${c.creditor || c.name || "cand"}`} className="flex items-start gap-3 border border-jc-gold/15 px-3 py-2.5 cursor-pointer hover:border-jc-gold/40" data-testid={`candidate-${i}`}>
                <span className="text-jc-gold mt-0.5">{selected.has(i) ? <CheckSquare size={16}/> : <Square size={16}/>}</span>
                <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} className="hidden"/>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-jc-muted">
                    <span className={`px-2 py-0.5 rounded ${c.severity === "high" ? "bg-red-500/20 text-red-200" : c.severity === "medium" ? "bg-amber-500/20 text-amber-200" : "bg-jc-muted/20"}`}>{c.severity || "n/a"}</span>
                    <span>{c.item_type}</span>
                    <span>·</span>
                    <span className="text-jc-gold">{c.creditor_or_furnisher}</span>
                  </div>
                  <p className="mt-1 text-sm text-jc-white">{c.fcra_violation}</p>
                  {c.evidence_in_report && <p className="mt-1 text-xs italic text-jc-muted">&ldquo;{c.evidence_in_report}&rdquo;</p>}
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Generate letters control */}
      {candidates.length > 0 && (
        <div className="mt-6 border-t border-jc-gold/15 pt-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-3">◆ Generate dispute letters</p>
          <div className="space-y-3">
            <label className="text-sm text-jc-white">
              <span className="block text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mb-1">Round</span>
              <select
                value={round}
                onChange={(e) => setRound(parseInt(e.target.value, 10))}
                className="w-full bg-jc-black border border-jc-gold/30 text-jc-white px-3 py-2 text-sm"
                data-testid="dispute-round-select"
              >
                {ROUNDS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>

            {(round === 1 || round === 3) && (
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mb-2">Bureaus</p>
                <div className="flex gap-2">
                  {BUREAUS.map((b) => (
                    <button
                      key={b}
                      onClick={() => toggleBureau(b)}
                      type="button"
                      data-testid={`bureau-${b}`}
                      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.18em] border ${bureaus.has(b) ? "border-jc-gold bg-jc-gold/10 text-jc-gold" : "border-jc-gold/20 text-jc-muted"}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              data-testid="dispute-draft-btn"
              disabled={drafting || selected.size === 0}
              onClick={() => onDraft({ indices: [...selected], round, bureaus: [...bureaus] })}
              className="jc-btn jc-btn-primary disabled:opacity-50"
            >
              {drafting ? <><Loader2 size={14} className="animate-spin"/> Drafting…</> : <><Sparkles size={14}/> Draft {selected.size} item(s) → Letters</>}
            </button>
            {draftMsg && <p className={`text-sm mt-2 ${draftMsg.startsWith("✔") ? "text-emerald-300" : "text-red-300"}`} data-testid="dispute-draft-msg">{draftMsg}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="border border-jc-gold/15 px-3 py-2.5">
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-jc-muted">{label}</p>
      <p className="text-xl font-display text-jc-white">{value}</p>
    </div>
  );
}
